import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { ChevronLeft, ChevronRight, Calendar, Clock, User, ArrowRight } from 'lucide-react';
import AppHeader from './AppHeader';
import InfoModal from './InfoModal';

// --- Tipos e Dados dos Planos (não mais necessário, créditos gerenciados no backend) ---

// Interface específica do Schedule que estende a interface base
interface ScheduleTherapist {
  id: string;
  nomePublico: string;
  title: string;
  status?: string;
  // Campos necessários para a lógica de agendamento
  sessionDuration?: number;
  horariosDisponiveis?: { [key: string]: { inicio: string, fim: string }[] };
}

interface TimeSlot {
  time: string;
  available: boolean;
}

// Função auxiliar para gerar slots de horário
const generateTimeSlots = (start: string, end: string, interval: number): string[] => {
  const slots = [];
  let currentTime = new Date(`1970-01-01T${start}:00`);
  const endTime = new Date(`1970-01-01T${end}:00`);

  while (currentTime < endTime) {
    slots.push(currentTime.toTimeString().substring(0, 5));
    currentTime.setMinutes(currentTime.getMinutes() + interval);
  }
  return slots;
};

const Schedule: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [therapists, setTherapists] = useState<ScheduleTherapist[]>([]);
  const [selectedTherapist, setSelectedTherapist] = useState<ScheduleTherapist | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const planId = new URLSearchParams(location.search).get('plan');
  const therapistId = new URLSearchParams(location.search).get('therapistId');

  const fetchTherapistById = (id: string) => {
    return therapists.find(t => t.id === id) || null;
  };

  // Buscar terapeutas disponíveis usando Cloud Function
  useEffect(() => {
    const fetchTherapists = async () => {
      setIsLoading(true);
      try {
        const getAvailableTherapists = httpsCallable(functions, 'getAvailableTherapistsForScheduling');
        const result = await getAvailableTherapists();
        const resultData = result.data as any;

        if (!resultData.success || !Array.isArray(resultData.therapists)) {
          throw new Error("Resposta inválida do servidor");
        }

        const therapistList = resultData.therapists as ScheduleTherapist[];
        setTherapists(therapistList);

        let _selectedTherapist = null;
        if (therapistId) {
          _selectedTherapist = therapistList.find(t => t.id === therapistId) || null;
          console.log("Therapist fetched by ID:", therapistId);
          if (_selectedTherapist) {
            setSelectedTherapist(_selectedTherapist);
          }
        }

        if (therapistList.length > 0 && !_selectedTherapist) {
          setSelectedTherapist(therapistList[0]);
        }
      } catch (err) {
        setError("Não foi possível carregar os terapeutas.");
        console.error("Erro ao carregar terapeutas:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTherapists();
  }, [therapistId]);

  // Restaurar a lógica original para buscar horários usando Firebase Callable Function
  useEffect(() => {
    if (!selectedTherapist || !selectedDate) {
      setAvailableSlots([]);
      return;
    };

    const fetchAndProcessSlots = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Chamar a Firebase Callable Function para buscar eventos do Google Agenda
        const getSlotsFunction = httpsCallable(functions, 'getAvailableSlots');
        const result = await getSlotsFunction({
          therapistId: selectedTherapist.id,
          date: selectedDate.toISOString()
        });
        const resultData = result.data as any;
        const { busySlots } = resultData as { busySlots: { start: string, end: string }[] };

        // Gerar todos os slots possíveis com base na jornada de trabalho do terapeuta
        const dayOfWeek = selectedDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        const workHours = selectedTherapist.horariosDisponiveis?.[dayOfWeek];
        const sessionDuration = selectedTherapist.sessionDuration || 60; // Padrão de 60 min

        if (!workHours || workHours.length === 0) {
          setAvailableSlots([]);
          setIsLoading(false);
          return;
        }

        let allPossibleSlots: string[] = [];
        workHours.forEach(period => {
          allPossibleSlots = [
            ...allPossibleSlots,
            ...generateTimeSlots(period.inicio, period.fim, sessionDuration)
          ];
        });

        // Filtrar os slots, removendo os que conflitam com os horários ocupados
        const finalSlots = allPossibleSlots.filter(slotTime => {
          const slotStart = new Date(`${selectedDate.toISOString().split('T')[0]}T${slotTime}:00`);
          const slotEnd = new Date(slotStart.getTime() + sessionDuration * 60000);

          // Verifica se o slot está no futuro
          if (slotStart < new Date()) {
            return false;
          }

          return !busySlots.some(busy => {
            const busyStart = new Date(busy.start);
            const busyEnd = new Date(busy.end);
            // Verifica se há sobreposição
            return slotStart < busyEnd && slotEnd > busyStart;
          });
        });

        setAvailableSlots(finalSlots.map(time => ({ time, available: true })));

      } catch (err) {
        console.error("Erro ao buscar horários:", err);
        setError("Não foi possível carregar os horários. O terapeuta pode não ter conectado a agenda.");
        setAvailableSlots([]);
      } finally {
        setIsLoading(false);
        setSelectedSlot(null);
      }
    };

    fetchAndProcessSlots();
  }, [selectedTherapist, selectedDate]);

  // FUNÇÃO ATUALIZADA: handlePayment para agendamento de constelação
  const handlePayment = async () => {
    // Limpar erros anteriores
    setError(null);

    // Validações básicas
    if (!user) {
      setError("Erro: Utilizador não está autenticado. Por favor, faça login novamente.");
      return;
    }

    if (!selectedTherapist) {
      setError("Erro: Nenhum terapeuta foi selecionado. Por favor, escolha um terapeuta.");
      return;
    }

    if (!selectedDate) {
      setError("Erro: Nenhuma data foi selecionada. Por favor, escolha uma data.");
      return;
    }

    if (!selectedSlot) {
      setError("Erro: Nenhum horário foi selecionado. Por favor, escolha um horário.");
      return;
    }

    if (!planId) {
      setError("Erro: Plano não identificado. Por favor, volte à seleção de planos.");
      return;
    }

    setIsLoading(true);

    try {
      // Criação da data/hora do agendamento
      const appointmentDateTime = new Date(selectedDate);
      const [hours, minutes] = selectedSlot.split(':');
      appointmentDateTime.setHours(Number(hours), Number(minutes), 0, 0);

      // Validação adicional da data
      if (appointmentDateTime < new Date()) {
        throw new Error("A data/hora selecionada já passou. Por favor, escolha um horário futuro.");
      }

      // Obter duração da sessão
      const sessionDuration = selectedTherapist.sessionDuration || 60;

      // Verificar se Firebase Functions está disponível
      if (!functions) {
        throw new Error("Serviço de agendamento não está disponível. Tente recarregar a página.");
      }

      // Chamar a função do backend para criar o agendamento
      const createAppointment = httpsCallable(functions, 'createConstellationAppointment');

      let result: any;
      try {
        result = await createAppointment({
          therapistId: selectedTherapist.id,
          appointmentDateTime: appointmentDateTime.toISOString(),
          sessionDuration: sessionDuration
        });
      } catch (functionError: any) {
        console.error("Erro na Firebase Function:", {
          code: functionError?.code,
          message: functionError?.message,
          details: functionError?.details
        });

        // Tratar erros específicos da função
        if (functionError?.code === 'functions/unauthenticated') {
          throw new Error("Erro de autenticação. Por favor, faça login novamente.");
        } else if (functionError?.code === 'functions/invalid-argument') {
          throw new Error(`Dados inválidos: ${functionError?.message}`);
        } else if (functionError?.code === 'functions/failed-precondition') {
          throw new Error(functionError?.message || "Você não tem créditos suficientes.");
        } else if (functionError?.code === 'functions/internal') {
          throw new Error("Erro interno do servidor. Tente novamente.");
        } else if (functionError?.code === 'functions/unavailable') {
          throw new Error("Serviço temporariamente indisponível. Tente novamente em alguns minutos.");
        } else {
          throw new Error(`Erro no agendamento: ${functionError?.message || 'Erro desconhecido'}`);
        }
      }

      // Processar resposta da função
      const responseData = result.data as { success?: boolean; appointmentId?: string; message?: string };

      if (!responseData) {
        throw new Error("Resposta vazia do servidor.");
      }

      if (responseData.success === false) {
        console.error("Function retornou erro:", responseData.message);
        throw new Error(responseData.message || "Erro ao criar agendamento");
      }

      if (responseData.success !== true) {
        console.error("Resposta inesperada da função:", responseData);
        throw new Error("Resposta inesperada do servidor.");
      }

      const { appointmentId } = responseData;

      console.log("Agendamento criado com sucesso:", appointmentId);

      // Mostrar modal de sucesso
      setShowSuccessModal(true);

    } catch (err: any) {
      console.error("Erro no processo de agendamento:", err);

      // Tratamento de erros mais específico
      let errorMessage = "Não foi possível criar o agendamento.";

      if (err?.message) {
        errorMessage = err.message;
      } else if (err?.code) {
        switch (err.code) {
          case 'functions/unauthenticated':
            errorMessage = "Problema de autenticação. Faça login novamente.";
            break;
          case 'functions/invalid-argument':
            errorMessage = "Dados inválidos. Verifique as informações inseridas.";
            break;
          case 'functions/failed-precondition':
            errorMessage = "Você não tem créditos suficientes para este agendamento.";
            break;
          case 'functions/internal':
            errorMessage = "Erro interno do servidor. Tente novamente.";
            break;
          case 'functions/unavailable':
            errorMessage = "Serviço temporariamente indisponível. Tente em alguns minutos.";
            break;
          default:
            errorMessage = `Erro: ${err.code}`;
        }
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
      console.log("Processo de agendamento finalizado");
    }
  };

  // Funções do Calendário
  const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const totalDays = daysInMonth(year, month);
    const firstDay = firstDayOfMonth(year, month);
    const calendarDays = [];

    for (let i = 0; i < firstDay; i++) {
      calendarDays.push(<div key={`empty-${i}`} className="p-2"></div>);
    }

    for (let day = 1; day <= totalDays; day++) {
      const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
      const isSelected = selectedDate?.toDateString() === new Date(year, month, day).toDateString();
      const currentDay = new Date(year, month, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to compare only dates

      // Condição 1: Data não está no passado
      const isNotInPast = currentDay >= today;

      // Condição 2: Terapeuta tem horários disponíveis para este dia da semana
      const dayOfWeek = currentDay.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      const workingHours = selectedTherapist?.horariosDisponiveis?.[dayOfWeek];
      const hasWorkingHours = workingHours && workingHours.length > 0;

      // Se não há terapeuta selecionado, considerar como não disponível
      const isAvailable = selectedTherapist && isNotInPast && hasWorkingHours;

      calendarDays.push(
        <button
          key={day}
          disabled={!isAvailable}
          onClick={() => isAvailable && setSelectedDate(new Date(year, month, day))}
          className={`p-2 text-center rounded-full transition-colors w-10 h-10
            ${!isAvailable ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-teal-100'}
            ${isToday ? 'font-bold text-teal-600' : ''}
            ${isSelected ? 'bg-teal-600 text-white' : ''}
          `}
        >
          {day}
        </button>
      );
    }

    return (
      <div className="grid grid-cols-7 gap-2 place-items-center">
        {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => <div key={i} className="font-bold text-gray-500">{d}</div>)}
        {calendarDays}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <AppHeader />
      <div className="container mx-auto px-4 max-w-4xl mt-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">Agende sua Sessão</h1>
        <div className="bg-white p-8 rounded-2xl shadow-lg space-y-8">
          <div>
            <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center gap-2"><User />Selecione o Terapeuta</h2>
            {therapists.length > 0 ? (
              <select
                value={selectedTherapist?.id || ''}
                onChange={(e) => setSelectedTherapist(fetchTherapistById(e.target.value))}
                className="w-full p-3 border border-gray-300 rounded-lg"
              >
                {therapists.map(t => <option key={t.id} value={t.id}>{t.nomePublico} {t.title ? '-' : ''} {t.title}</option>)}
              </select>
            ) : <p>Carregando terapeutas...</p>}
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center gap-2"><Calendar />Escolha a Data</h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <button onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)))}><ChevronLeft /></button>
                <span className="font-bold text-lg">{currentDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' })}</span>
                <button onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)))}><ChevronRight /></button>
              </div>
              {renderCalendar()}
            </div>
          </div>

          {selectedDate && (
            <div>
              <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center gap-2"><Clock />Escolha o Horário</h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                {isLoading ? <p>A verificar horários...</p> : availableSlots.map(slot => (
                  <button
                    key={slot.time}
                    onClick={() => setSelectedSlot(slot.time)}
                    className={`p-3 border rounded-lg transition-colors ${selectedSlot === slot.time ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-300 hover:bg-teal-100'
                      }`}
                  >
                    {slot.time}
                  </button>
                ))}
                {!isLoading && availableSlots.length === 0 && <p className="col-span-full text-center text-gray-500">Nenhum horário disponível para esta data.</p>}
              </div>
            </div>
          )}

          {selectedSlot && (
            <div className="bg-teal-50 border-l-4 border-teal-500 p-6 rounded-lg mt-8">
              <h3 className="text-xl font-bold text-teal-800 mb-4">Resumo do Agendamento</h3>
              <div className="space-y-2">
                <p><strong>Terapeuta:</strong> {selectedTherapist?.nomePublico}</p>
                <p><strong>Data:</strong> {selectedDate?.toLocaleDateString('pt-BR')}</p>
                <p><strong>Horário:</strong> {selectedSlot}</p>
              </div>

              {error && (
                <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <button
                onClick={handlePayment}
                disabled={isLoading}
                className="w-full mt-6 bg-teal-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-teal-700 disabled:bg-teal-400 transition flex items-center justify-center gap-2"
              >
                {isLoading ? 'Processando...' : 'Confirmar'} <ArrowRight />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Sucesso */}
      <InfoModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate('/painel');
        }}
        title="Agendamento Confirmado!"
        message="Sua sessão de constelação foi agendada com sucesso. Você pode visualizar os detalhes no seu painel."
        variant="success"
        buttonText="Ir para o Painel"
      />
    </div>
  );
};

export default Schedule;